import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { UploadFileTypes } from "../../constants";
import { useProject, useUpload } from "../../hooks";
import { Button, SubmitButton } from "../buttons/Buttons";
import FormWrapper from "../forms/Form";
import ModalWrapper from "./Modal";
import styles from "./Modal.module.css";

function AddMediaModal({ handleClose, isVisible, isUpdate, media }) {
  const [newFile, setNewFile] = useState(null);
  const { uploadFile } = useUpload();
  const { addNewMedia, updateProjectMediaById } = useProject();
  const [searchParams] = useSearchParams();
  const [mediaName, setMediaName] = useState("");
  const [showError, setShowError] = useState(false);
  const [fileUploadStatus, setFileUploadStatus] = useState(false);

  const handleFileAdd = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      setNewFile(selectedFile);
    }

    if (e.target.files[0].size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB.");
      return;
    }
  };

  useEffect(() => {
    if (isUpdate) setMediaName(media.media_title);
  }, [isUpdate]);

  useEffect(() => {
    setShowError(false);
  }, [mediaName]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    const projectId = searchParams.get("id");
    if (!newFile) return toast.error("Please select a file to upload.");
    if (!mediaName) return setShowError(true);

    setFileUploadStatus(true);
    const { id } = await handleFileUpload();
    if (!id) return;

    if (isUpdate) {
      const { error, success } = await updateProjectMediaById(media.id, { file_id: id, media_title: mediaName });
      setFileUploadStatus(false);
      if (!success) return toast.error(error);
      toast.success("Media updated successfully.");
      handleClose();
      return;
    }

    const { error, success } = await addNewMedia({ project_id: projectId, file_id: id, media_title: mediaName });
    setFileUploadStatus(false);
    if (error) return toast.error(error);
    toast.success(success);
    handleClose();
  };

  const handleFileUpload = async () => {
    const { success, data, error } = await uploadFile(newFile, UploadFileTypes.VIDEOS);
    if (error) return toast.error(error);
    toast.success(success);
    return { id: data.file_id, url: data.url };
  };

  return (
    <ModalWrapper isVisible={isVisible} onClose={handleClose} name="add_media_modal" level={2} fit>
      <FormWrapper id="add_media_form" onSubmit={handleUpdate}>
        <div className="section_title_wrapper">
          <h3 className="section_title">{isUpdate ? `Update ${media.media_title}` : "Add New Media"}</h3>
          <label htmlFor="new_file_header" className={styles.upload_icon_button}>
            <Icon icon="iconoir:add-media-image" inline />
            <input
              type="file"
              id="new_file_header"
              name="new_file_header"
              accept={"video/mp4, video/mkv"}
              onChange={handleFileAdd}
              hidden
            />
            <span className={styles.add_media_label}>Add New Media</span>
          </label>
        </div>
        <div className="input_wrapper">
          <input
            type="text"
            className="input full"
            placeholder="Enter Media Name"
            onChange={(e) =>
              setMediaName(e.target.value.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1)))
            }
            value={mediaName}
            required
            maxLength={50}
          />
          <span className={`error small ${showError ? "visible" : "hide"}`}>Please enter media name</span>
        </div>
        <div className="column">
          {newFile ? (
            <div className={`${styles.image_grid} ${styles.uploaded_video_wrapper}`}>
              <video controls className={styles.uploaded_image}>
                <source src={URL.createObjectURL(newFile)} type="video/mp4" />
              </video>
              <Button
                onClick={() => setNewFile(null)}
                icon="ic:outline-delete"
                iconPosition="start"
                className="delete_image_button"
                disabled={fileUploadStatus}
              />
            </div>
          ) : (
            <div className="column center">
              <div className={styles.upload_input_wrapper}>
                <Icon icon="iconoir:add-media-image" className={styles.upload_icon} />
              </div>
              <div>
                <label htmlFor="new_file" className={styles.upload_link}>
                  Browse
                  <input
                    type="file"
                    id="new_file"
                    name="new_file"
                    accept="video/mp4, video/mkv"
                    onChange={handleFileAdd}
                    hidden
                  />
                </label>
                <span> your files</span>
              </div>
            </div>
          )}
        </div>
        <div className="row center">
          <Button className="ghost_outline_round" onClick={handleClose} disabled={fileUploadStatus}>
            Cancel
          </Button>
          <SubmitButton className="cta" form="add_media_form" disabled={fileUploadStatus}>
            Upload
          </SubmitButton>
        </div>
      </FormWrapper>
    </ModalWrapper>
  );
}

export default AddMediaModal;
